/**
 * The newcomer: a figure with a lantern, drawn as shapes so it stays crisp at any size and light on a phone. Shared by
 * the offer at the gate (`views/newcomers-offer.ts`), the landing's scene card and the figure that stands at a
 * district in the town (`people-layer.ts`). `gateScene` frames it in the town's gate arch; `figure` is the person alone.
 * The CSS in `styles.css` (`.nc-scene`, `.nc-figure`, `.nc-lantern`, `.nc-halo`) animates by `data-mode`.
 */
import { svg } from 'lit';

/**
 * The gradients a figure needs. Ids are fixed per drawing, never minted per render, and distinct between drawings that
 * can share a page: a duplicate whose first copy is hidden (`display: none`, an off-screen town figure) would leave the
 * others' fills empty.
 */
function defs(id: string) {
  return svg`<defs>
    <radialGradient id="${id}l" cx="50%" cy="50%" r="50%"><stop offset="0" stop-color="#fffbe6"/><stop offset="1" stop-color="#ffb15a"/></radialGradient>
    <linearGradient id="${id}s" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#5a4a66"/><stop offset="1" stop-color="#2a2238"/></linearGradient>
    <radialGradient id="${id}h" cx="50%" cy="50%" r="50%"><stop offset="0" stop-color="#ffd27a" stop-opacity=".9"/><stop offset=".55" stop-color="#ff9a4a" stop-opacity=".3"/><stop offset="1" stop-color="#ff9a4a" stop-opacity="0"/></radialGradient>
  </defs>`;
}

/** The person: head, body, the lantern arm, the lantern and its halo, in a 100×100 box standing on y=94. */
export function figureBody(id: string) {
  return svg`<g class="nc-figure">
    <circle class="nc-halo" cx="72" cy="83" r="19" fill="url(#${id}h)"/>
    <circle cx="50" cy="50" r="7.5" fill="url(#${id}s)" stroke="#ffd9a0" stroke-opacity=".55" stroke-width="1.3"/>
    <path d="M37 94 V69 Q37 58 50 58 Q63 58 63 69 V94Z" fill="url(#${id}s)" stroke="#ffd9a0" stroke-opacity=".5" stroke-width="1.3"/>
    <path class="nc-arm" d="M61 65 L72 73" stroke="#6a5a78" stroke-width="4.5" stroke-linecap="round"/>
    <g class="nc-lantern">
      <path d="M72 73 V77" stroke="#ffd9a0" stroke-width="1.8" stroke-linecap="round"/>
      <rect x="66" y="77" width="12" height="15" rx="3.5" fill="url(#${id}l)"/>
      <circle cx="72" cy="84.5" r="2.8" fill="#fff"/>
    </g>
  </g>`;
}

export type GateMode = 'at-gate' | 'still' | 'arriving';

/**
 * The gate scene: the arch, the figure with its lantern, its glow on the ground. `still` dims the lantern (held by the
 * review, the landing's heads); `arriving` is Meet them's wait.
 */
export function gateScene(mode: GateMode) {
  const id = `ncg-${mode}-`;
  if (mode === 'arriving') return arrivingScene(id);
  return svg`<svg class="nc-scene" data-mode=${mode} viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
  ${defs(id)}
  <path class="nc-glow" d="M50 94 L6 94 Q18 58 50 54 Q82 58 94 94Z" fill="url(#${id}l)" opacity=".22"/>
  <path d="M14 96 V46 Q14 18 50 16 Q86 18 86 46 V96" fill="none" stroke="#ffd9a0" stroke-width="4.5" stroke-linecap="round"/>
  <path d="M22 96 V48 Q22 27 50 25 Q78 27 78 48 V96" fill="none" stroke="#ffd9a0" stroke-width="1.6" opacity=".4"/>
  <rect x="6" y="94" width="88" height="4" rx="2" fill="#ffd9a0" opacity=".6"/>
  ${figureBody(id)}
</svg>`;
}

/**
 * The arrival stage (Meet them's wait): a wide strip, the gate at the start side and a dashed plot at the far side, the
 * figure walking from one to the other and starting again at the gate, a faint glow trail behind it. Drawn left to
 * right; the CSS mirrors the whole stage in a right-to-left interface.
 */
function arrivingScene(id: string) {
  return svg`<svg class="nc-scene nc-arrive" data-mode="arriving" viewBox="0 0 320 90" width="100%" height="100%" preserveAspectRatio="xMidYMax meet" aria-hidden="true">
  ${defs(id)}
  <rect x="4" y="85" width="312" height="2.5" rx="1.25" fill="#ffd9a0" opacity=".3"/>
  <g transform="translate(4 2) scale(.84)">
    <path d="M14 98 V46 Q14 18 50 16 Q86 18 86 46 V98" fill="none" stroke="#ffd9a0" stroke-width="4.5" stroke-linecap="round"/>
    <path d="M22 98 V48 Q22 27 50 25 Q78 27 78 48 V98" fill="none" stroke="#ffd9a0" stroke-width="1.6" opacity=".4"/>
  </g>
  <path class="nc-plot" d="M252 85 V58 L280 40 L308 58 V85" fill="none" stroke="#ffd9a0" stroke-width="1.8" stroke-dasharray="5 4" stroke-linejoin="round" opacity=".55"/>
  <rect class="nc-trail" x="46" y="83" width="206" height="3" rx="1.5" fill="url(#${id}l)" opacity=".35"/>
  <g class="nc-walker"><g transform="translate(4 6) scale(.84)">${figureBody(id)}</g></g>
</svg>`;
}

export type FigureState = 'waiting' | 'home';

/**
 * The person alone, for the town: `waiting` dim with the lantern lit, `home` lit. `key` tells figures on one page apart
 * (the district's id in the town, `offer` at the gate).
 */
export function figure(state: FigureState, key: string) {
  const id = `ncf-${state}-${key.replace(/[^\w-]/g, '_')}-`;
  return svg`<svg class="nc-person" data-state=${state} viewBox="30 38 50 60" width="100%" height="100%" aria-hidden="true">
  ${defs(id)}
  ${figureBody(id)}
</svg>`;
}
